import React from "react";
import {
  AvailableCatalog,
  EvaluationCriteria,
  EvaluationType,
} from "../../../model_gen/evaluation";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { BrainCircuitIcon } from "lucide-react";
import {
  FrameworkTabs,
  FrameworkHeader,
  FrameworkCriteriaList,
} from "./FrameworkCriteriaList";
import CustomCriteriaSection from "./CustomCriteriaSection";

interface SubjectiveCriteriaCardProps {
  catalog: AvailableCatalog | null;
  companyCriteria: EvaluationCriteria[];
  selectedCriteriaIds: number[];
  onCriteriaChange: (ids: number[]) => void;
  toggleCriteria: (id: number) => void;
  activeFramework: string;
  setActiveFramework: (fw: string) => void;
  showCustomForm: boolean;
  setShowCustomForm: (v: boolean) => void;
  customName: string;
  setCustomName: (v: string) => void;
  customInstructions: string;
  setCustomInstructions: (v: string) => void;
  customScoringType: "numeric" | "boolean";
  onScoringTypeChange: (type: "numeric" | "boolean") => void;
  editingCriteriaId: number | null;
  loading: boolean;
  onCreateCustom: () => void;
  onUpdateCustom: () => void;
  resetCustomForm: () => void;
  startEditCriteria: (crit: EvaluationCriteria) => void;
}

const SubjectiveCriteriaCard: React.FC<SubjectiveCriteriaCardProps> = ({
  catalog,
  companyCriteria,
  selectedCriteriaIds,
  onCriteriaChange,
  toggleCriteria,
  activeFramework,
  setActiveFramework,
  showCustomForm,
  setShowCustomForm,
  customName,
  setCustomName,
  customInstructions,
  setCustomInstructions,
  customScoringType,
  onScoringTypeChange,
  editingCriteriaId,
  loading,
  onCreateCustom,
  onUpdateCustom,
  resetCustomForm,
  startEditCriteria,
}) => {
  const frameworkOptions =
    catalog?.frameworks?.map((fw) => ({
      id: fw.id,
      name: fw.name,
      description: fw.description,
    })) ?? [];

  const activeFrameworkCatalog = catalog?.frameworks?.find(
    (fw) => fw.id === activeFramework
  );

  const activeFrameworkCriteria = companyCriteria.filter(
    (c) =>
      c.type === EvaluationType.LLM_JUDGE && c.framework === activeFramework
  );

  const activeFrameworkIds = activeFrameworkCriteria
    .map((c) => c.id!)
    .filter(Boolean);

  const selectedCountForFramework = (fwId: string): number =>
    companyCriteria.filter(
      (c) =>
        c.type === EvaluationType.LLM_JUDGE &&
        c.framework === fwId &&
        c.id != null &&
        selectedCriteriaIds.includes(c.id)
    ).length;

  const allFrameworkSelected =
    activeFrameworkIds.length > 0 &&
    activeFrameworkIds.every((id) => selectedCriteriaIds.includes(id));

  const toggleAllFramework = () => {
    if (allFrameworkSelected) {
      onCriteriaChange(
        selectedCriteriaIds.filter((id) => !activeFrameworkIds.includes(id))
      );
    } else {
      const newIds = new Set([...selectedCriteriaIds, ...activeFrameworkIds]);
      onCriteriaChange(Array.from(newIds));
    }
  };

  const customCriteria = companyCriteria.filter(
    (c) => c.type === EvaluationType.LLM_JUDGE && c.framework === "CUSTOM"
  );

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base font-gc-med">
          <BrainCircuitIcon className="w-4 h-4 text-maitai-green" />
          Subjective
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <FrameworkTabs
          frameworkOptions={frameworkOptions}
          activeFramework={activeFramework}
          setActiveFramework={setActiveFramework}
          selectedCountForFramework={selectedCountForFramework}
        />

        {activeFramework !== "CUSTOM" && activeFrameworkCatalog && (
          <FrameworkHeader
            description={activeFrameworkCatalog.description}
            hasIds={activeFrameworkIds.length > 0}
            allSelected={allFrameworkSelected}
            toggleAll={toggleAllFramework}
          />
        )}

        {activeFramework !== "CUSTOM" && (
          <FrameworkCriteriaList
            criteria={activeFrameworkCriteria}
            catalogEntry={activeFrameworkCatalog}
            selectedCriteriaIds={selectedCriteriaIds}
            toggleCriteria={toggleCriteria}
          />
        )}

        {activeFramework === "CUSTOM" && (
          <CustomCriteriaSection
            customCriteria={customCriteria}
            selectedCriteriaIds={selectedCriteriaIds}
            toggleCriteria={toggleCriteria}
            editingCriteriaId={editingCriteriaId}
            startEditCriteria={startEditCriteria}
            showCustomForm={showCustomForm}
            setShowCustomForm={setShowCustomForm}
            customName={customName}
            setCustomName={setCustomName}
            customInstructions={customInstructions}
            setCustomInstructions={setCustomInstructions}
            customScoringType={customScoringType}
            onScoringTypeChange={onScoringTypeChange}
            loading={loading}
            onSave={editingCriteriaId ? onUpdateCustom : onCreateCustom}
            resetCustomForm={resetCustomForm}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default SubjectiveCriteriaCard;
